import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ErrorBoundary, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { useLocalization } from '@shared-ui/localization-context';

import { SleepingArrangement, SleepingArrangementSpacesViewModel } from 'models';

import { classNames } from 'uitk-react-utils';
import { UitkCard, UitkCardContent, UitkCardTextSegment } from 'uitk-react-cards';
import { UitkCarousel } from 'uitk-react-carousel';

import { Lazy } from 'components/common/lazy/lazy';
import { Space } from 'components/infosite/property-sleeping-arrangements/internals/space-card';

export interface SleepingArrangementsProps {
  analytics?: AnalyticsStore;
  isDialog?: boolean;
  sleepingArrangementsViewModel?: SleepingArrangementSpacesViewModel;
}

const MAX_SPACES = 4;

export const SleepingArrangementsWrapper = inject('currentHotel')(({ currentHotel }) => {
  const sleepingArrangementsViewModel = currentHotel && currentHotel.offers && currentHotel.offers.sleepingArrangementsViewModel;

  if (!sleepingArrangementsViewModel) {
    return null;
  }

  return (
    <ErrorBoundary label="hotel.information.sleepingArrangements">
      <Lazy>
        <Lazy.Placeholder>
          <div data-stid="sleeping-arrangements-placeholder" />
        </Lazy.Placeholder>
        <Lazy.Loaded>
          <SleepingArrangements sleepingArrangementsViewModel={sleepingArrangementsViewModel} />
        </Lazy.Loaded>
      </Lazy>
    </ErrorBoundary>
  );
});

export const SleepingArrangements: React.FunctionComponent<SleepingArrangementsProps> = inject('analytics')(
  ({ analytics, isDialog, sleepingArrangementsViewModel }) => {
    React.useEffect(() => {
      if (analytics && typeof analytics.trackEvent === 'function') {
        analytics.trackEvent('SleepingArrangements.display', 'Display', analytics.omnitureData);
      }
    }, []);

    if (
      !sleepingArrangementsViewModel ||
      !sleepingArrangementsViewModel.sleepingArrangements ||
      sleepingArrangementsViewModel.sleepingArrangements.length === 0
    ) {
      return null;
    }

    const { title, sleepingArrangements } = sleepingArrangementsViewModel;

    if (isDialog) {
      return (
        <>
          <SleepingArrangementsTitle className="uitk-type-heading-400" title={title} />
          <SleepingArrangmentsSimpleView isDialog={isDialog} sleepingArrangements={sleepingArrangements} />
        </>
      );
    }

    return (
      <div
        className="all-t-margin-three s-x-padding-three all-x-padding-six m-x-padding-zero xl-x-padding-zero"
        data-stid="sleeping-arrangements"
      >
        <UitkCard>
          <UitkCardContent>
            <UitkCardTextSegment>
              <SleepingArrangementsTitle className="uitk-type-heading-600" title={title} />
              <Viewport>
                <ViewSmall>
                  <SleepingArrangmentsSimpleView sleepingArrangements={sleepingArrangements} />
                </ViewSmall>
                <ViewMedium>
                  <SleepingArrangementsViewMedium analytics={analytics} sleepingArrangements={sleepingArrangements} />
                </ViewMedium>
              </Viewport>
            </UitkCardTextSegment>
          </UitkCardContent>
        </UitkCard>
      </div>
    );
  }
);
SleepingArrangements.displayName = 'SleepingArrangements';

interface SleepingArrangementsViewMediumProps {
  analytics?: AnalyticsStore;
  sleepingArrangements?: SleepingArrangement[];
}

const SleepingArrangementsViewMedium: React.FunctionComponent<SleepingArrangementsViewMediumProps> = ({
  analytics,
  sleepingArrangements
}) => {
  if (sleepingArrangements.length > MAX_SPACES) {
    return <SleepingArrangementsCarousel analytics={analytics} sleepingArrangements={sleepingArrangements} />;
  } else {
    return (
      <div className="uitk-grid all-x-gutter-three">
        <SleepingArrangmentsSimpleView className="uitk-flex uitk-cell all-cell-1-4" sleepingArrangements={sleepingArrangements} />
      </div>
    );
  }
};

interface SleepingArrangementsTitleProps {
  className?: string;
  title?: string;
}

const SleepingArrangementsTitle: React.FunctionComponent<SleepingArrangementsTitleProps> = ({ className = '', title }) =>
  title && <h3 className={className}>{title}</h3>;

interface SleepingArrangmentsSimpleViewProps {
  className?: string;
  isDialog?: boolean;
  sleepingArrangements?: SleepingArrangement[];
}

const SleepingArrangmentsSimpleView: React.FunctionComponent<SleepingArrangmentsSimpleViewProps> = ({
  className = '',
  isDialog = false,
  sleepingArrangements
}) => {
  const spaceCardClassName = classNames('all-t-margin-three', className);

  return (
    <>
      {sleepingArrangements.map((sleepingArrangement, index) => (
        <div className={spaceCardClassName} key={`space-${index}`}>
          <Space isDialog={isDialog} sleepingArrangement={sleepingArrangement} />
        </div>
      ))}
    </>
  );
};

const SleepingArrangementsCarousel: React.FunctionComponent<SleepingArrangementsViewMediumProps> = ({
  analytics,
  sleepingArrangements
}) => {
  const { formatText } = useLocalization();
  const carouselProps = {
    analytics: analytics && analytics.setupTracker('BTO', 'See more'),
    itemsVisible: { lg: 4, md: 4, sm: 1 },
    buttonText: {
      nextButton: formatText('carousel.nextItem'),
      prevButton: formatText('carousel.previousItem')
    },
    pageBy: 1,
    peekWidth: 12,
    className: 'all-t-margin-three',
    carouselName: 'sleeping-arrangements'
  };

  return (
    <UitkCarousel {...carouselProps}>
      {sleepingArrangements.map((sleepingArrangement, index) => (
        <Space key={`space-${index}`} sleepingArrangement={sleepingArrangement} />
      ))}
    </UitkCarousel>
  );
};
